import { Ingredient } from '@cf-shared/data-model/ingredient';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { Nutrients } from '@cf-shared/data-model/food';
import { deepClone } from '@cf-shared/deep-helpers';

/**
 * Making one attempt at copying a whole past day onto the day being planned all-or-nothing
 * (`df-9f2f7eb82990`).
 *
 * `tryToCopyEntireDayAsync` in `meal-plan.ts` fills the day's meals one at a time from a past
 * day, and gives up the moment one meal has no template there — or, having filled them all,
 * when the day's nutrient match comes out under the cut-off. Both of those are the app deciding
 * NOT to use that day. Until this existed, neither of them undid the meals already filled, and
 * everything downstream then treated those leftovers as meals the user had planned:
 *
 *  - `areAllMealsUnplanned()`, the guard that allows the whole-day route at all, is asked once
 *    before the first attempt and was no longer true by the second;
 *  - `tryToCombineMealsForTodayAsync`, the fallback, deliberately skips a meal that already has
 *    ingredients, so what a rejected day left behind became the user's final plan.
 *
 * These are the fields one attempt writes: `fillMealFromOtherMeal` sets `ingredients` and
 * `basedOnFoodId`, the copy loop sets `autoPlanned`, and the refresh that follows recalculates
 * `nutrients`. Put those four back and the day is exactly as the attempt found it.
 *
 * "Exactly" includes a field the meal did not have: a damaged or old-shaped document can arrive
 * with no `ingredients` and no `nutrients` at all, and the rest of the page guards for that
 * (`mealLog.ingredients && mealLog.ingredients.length > 0`). Writing `null` over an absent field
 * while putting a meal back would be this code introducing the shape it is meant to preserve, so
 * each of the four fields remembers whether the meal had it, and an absent field is restored to
 * absent.
 *
 * They live here, apart from the page, because the page pulls in most of the app's dependency
 * graph and cannot be constructed in a unit test — see `meal-tuner-crash-guards.test.ts` beside
 * it. Kept pure, they are tested directly in `meal-plan-day-copy-rollback.test.ts`.
 */
export interface MealDayCopyRollbackState {
  /** The remembered value, and whether the meal had the field at all — absent is restored to absent. */
  ingredients: Ingredient[] | undefined;
  hadIngredients: boolean;
  nutrients: Nutrients | undefined;
  hadNutrients: boolean;
  basedOnFoodId: string | undefined;
  hadBasedOnFoodId: boolean;
  autoPlanned: boolean | undefined;
  hadAutoPlanned: boolean;
}

/**
 * Remember what these meals hold, before an attempt at copying a past day onto them.
 *
 * The two object-valued fields are cloned, so that filling the meal afterwards cannot reach into
 * what we remembered. `deepClone` answers `null` for an absent value, so it is only called for a
 * field the meal actually has — the `had…` flag carries the rest.
 */
export function captureMealsBeforeDayCopy(meals: Log_Meal[]): MealDayCopyRollbackState[] {
  return meals.map(meal => ({
    ingredients: 'ingredients' in meal ? deepClone(meal.ingredients) : undefined,
    hadIngredients: 'ingredients' in meal,
    nutrients: 'nutrients' in meal ? deepClone(meal.nutrients) : undefined,
    hadNutrients: 'nutrients' in meal,
    basedOnFoodId: meal.basedOnFoodId,
    hadBasedOnFoodId: 'basedOnFoodId' in meal,
    autoPlanned: meal.autoPlanned,
    hadAutoPlanned: 'autoPlanned' in meal,
  }));
}

/**
 * Put those meals back exactly as `captureMealsBeforeDayCopy` found them.
 *
 * The caller builds both arrays from one meal list, so they pair up index by index; anything the
 * shorter of the two does not reach is left alone rather than guessed at. Whether the day's meal
 * list was replaced underneath a running attempt is a different question, and the page asks it
 * against its own live list — this function cannot see that.
 */
export function restoreMealsAfterFailedDayCopy(meals: Log_Meal[], remembered: MealDayCopyRollbackState[]): void {
  const pairs = Math.min(meals.length, remembered.length);
  for (let i = 0; i < pairs; i++) {
    const meal = meals[i];
    const state = remembered[i];
    // cloned again, so restoring twice from one snapshot cannot hand the same arrays to two meals
    restoreField(meal, 'ingredients', state.hadIngredients, () => deepClone(state.ingredients));
    restoreField(meal, 'nutrients', state.hadNutrients, () => deepClone(state.nutrients));
    restoreField(meal, 'basedOnFoodId', state.hadBasedOnFoodId, () => state.basedOnFoodId);
    restoreField(meal, 'autoPlanned', state.hadAutoPlanned, () => state.autoPlanned);
  }
}

/** Put one field back, or take it off the meal again when it was not there before. */
function restoreField<K extends keyof Log_Meal>(meal: Log_Meal, field: K, wasPresent: boolean, value: () => Log_Meal[K]) {
  if (wasPresent) {
    meal[field] = value();
  } else {
    delete meal[field];
  }
}
